import { arrow, cross, lShape, plus, star } from "fast-2d";
import { Camera } from "../render/Camera";
import type { Demo, Scene } from "./types";
import { makeWorld, floor, walls, rng } from "./helpers";

type ConcaveKind = "star" | "plus" | "cross" | "l" | "arrow";

export const concave: Demo = {
	id: "concave",
	title: "Concave rigid bodies",
	blurb:
		"Stars, pluses, crosses, L-shapes and arrows fall as single rigid bodies, nesting and catching on their concave edges inside a narrow bin.",
	category: "Stacking & Stress",
	build(): Scene {
		const world = makeWorld();
		floor(world, 20, 2.5);
		walls(world, 7, 18, 1);

		const random = rng(0xc0ca7e);
		const kinds: readonly ConcaveKind[] = ["star", "plus", "cross", "l", "arrow"];
		const cols = 6;
		const count = 36;
		// These pieces span up to ~1.45 units across, so the lattice has to be wider
		// than their footprint or neighbours spawn already interpenetrating and visibly
		// shove their sharp edges through each other on the first frame.
		const spacingX = 1.85;
		const spacingY = 1.6;

		for (let i = 0; i < count; i++) {
			const col = i % cols;
			const row = Math.floor(i / cols);
			const x = (col - (cols - 1) / 2) * spacingX + (random() - 0.5) * 0.16;
			const y = -2.2 - row * spacingY;
			const kind = kinds[(i + Math.floor(random() * kinds.length)) % kinds.length]!;
			const angle = random() * Math.PI * 2;
			const opts = {
				position: [x, y] as [number, number],
				angle: kind === "cross" ? angle + Math.PI / 4 : angle,
				velocity: [(random() - 0.5) * 0.22, 0.2 + random() * 0.2] as [number, number],
				angularVelocity: (random() - 0.5) * 0.8,
				restitution: 0.03,
				friction: 0.78,
				linearDamping: 0.02,
				angularDamping: 0.06,
			};

			switch (kind) {
				case "star":
					world.add(star({ ...opts, points: i % 3 === 0 ? 6 : 5, outerRadius: 0.62, innerRadius: 0.28 }));
					break;
				case "plus":
					world.add(plus({ ...opts, width: 1.25, height: 1.25, thickness: 0.42 }));
					break;
				case "cross":
					world.add(cross({ ...opts, width: 1.22, height: 1.22, thickness: 0.36 }));
					break;
				case "l":
					world.add(lShape({ ...opts, width: 1.28, height: 1.28, thickness: 0.42 }));
					break;
				case "arrow":
					world.add(arrow({ ...opts, length: 1.45, width: 0.98, headLength: 0.55, shaftWidth: 0.36 }));
					break;
			}
		}

		return {
			world,
			camera: new Camera(0, -5, 28),
			hint: "Each piece is one rigid body made from convex parts. Grab a star or arrow and wedge it into the pile.",
		};
	},
};